import { Header } from "@/components/layout/Header";
import { TokenForm } from "@/components/token/TokenForm";
import { FaqSection } from "@/components/faq/FaqSection";
import { LiquidityProvider } from "@/providers/LiquidityProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { XLogo } from "@/components/icons/XLogo";
import { Logo } from "@/components/layout/Logo";
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { BlogLayout } from "@/pages/blog/BlogLayout";
import { CreateTokenGuide } from "@/pages/blog/CreateTokenGuide";
import { TokenEconomics } from "@/pages/blog/TokenEconomics";
import { TokenSecurity } from "@/pages/blog/TokenSecurity";
import { AutomaticContractVerification } from "@/pages/blog/AutomaticContractVerification";
import { AddTokenLogo } from "@/pages/blog/AddTokenLogo";
import { LaunchOnPancakeSwap } from "@/pages/blog/LaunchOnPancakeSwap";
import { MatrixContractScanner } from "@/pages/blog/MatrixContractScanner";
import { TokenTools as TokenToolsBlog } from "@/pages/blog/TokenTools";
import { ContractLocker as ContractLockerBlog } from "@/pages/blog/ContractLocker";
import { SmartContractLocking } from "@/pages/blog/SmartContractLocking";
import { CreateMemeCoin } from "@/pages/blog/CreateMemeCoin";
import { CreateReflectionToken } from "@/pages/blog/CreateReflectionToken";
import { CreateAutoLiquidityToken } from "@/pages/blog/CreateAutoLiquidityToken";
import { CreateAutoBurnToken } from "@/pages/blog/CreateAutoBurnToken";
import { BaseLayout } from "@/components/layout/BaseLayout";
import { Footer } from "@/components/layout/Footer";
import { HomePage } from "@/pages/home";
import { CheckCircle2, ShieldCheck, Fuel } from 'lucide-react';
import { NetworkStatus } from "@/components/status/NetworkStatus";
import { SmartContractLocker } from "@/pages/smartcontractlocker";
import TokenTools from "@/pages/tokentools";
import { WalletButton } from "@/components/layout/WalletButton";
import { CreateTokenPage } from "@/pages/create";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { BurnSettingsProvider } from "@/providers/BurnSettingsProvider";
import { ToolsSettingsProvider } from "@/providers/ToolsSettingsProvider";
import { LockerSettingsProvider } from "@/providers/LockerSettingsProvider";
import { LiquiditySettingsProvider } from "@/providers/LiquiditySettingsProvider";
import { ContractScannerSettingsProvider } from "@/providers/ContractScannerSettingsProvider";
import { AdminLayout } from "@/pages/admin/components/AdminLayout";
import AdminPage from "@/pages/admin";
import MdbMatrixContractScan from "@/pages/MdbMatrixContractScan/MdbMatrixContractScan";
import { FirebaseFeeProvider } from "./providers/FirebaseFeeProvider";
import TokenLogoPage from "@/pages/tokenlogo";
import ErrorBoundary from "@/components/ErrorBoundary";
import { GoogleAnalytics } from './components/analytics/GoogleAnalytics';
import { GoogleTag } from './components/analytics/GoogleTag';
import { OrganizationSchema } from './components/seo/OrganizationSchema';
import BestColdStorageWallets from "@/pages/blog/BestColdStorageWallets";
import { BestCryptoProjectApp2025 } from "@/pages/blog/BestCryptoProjectApp2025";
import { PrivacyPolicy } from "@/pages/legal/PrivacyPolicy";
import { TermsOfService } from "@/pages/legal/TermsOfService";
import { About } from "@/pages/legal/About";
import { Contact } from "@/pages/legal/Contact";
import { RefundPolicy } from "@/pages/legal/RefundPolicy";
import { useEffect } from 'react';
import { fixMdbAppScriptLoading } from "@/utils/secureWeb3Provider";
import { applyMobileWalletFixes, isMobile, isWalletBrowser } from "@/utils/mobile-compatibility";
import { PageViewTracker } from '@/components/analytics/PageViewTracker';
import { SolanaTokenLauncher } from "@/pages/blog/SolanaTokenLauncher";
import { FreeSolanaTokens } from "@/pages/blog/FreeSolanaTokens";
import { SolanaTokenLauncher as SolanaTokenLauncherApp } from "@/pages/SolanaTokenLauncher";
import { SuiTokenLauncher } from "@/pages/SuiTokenLauncher";
import { UserAuthProvider } from "@/providers/UserAuthProvider";
import AccountPage from "@/pages/account";
import NFTPage from "@/pages/nft";
import MultisenderPage from "@/pages/multisender";
import LiquidityPage from "@/pages/liquidity";
import { useNetworkThemeSync } from "@/hooks/useNetworkThemeSync";

function App() {
  // Enable automatic theme switching based on network changes
  useNetworkThemeSync();

  useEffect(() => {
    // Apply script loading fix for CSP issues
    fixMdbAppScriptLoading();
    
    // Apply mobile wallet browser fixes
    applyMobileWalletFixes();
    
    // Log device information for debugging
    if (isMobile) {
      console.log('Mobile device detected', {
        userAgent: navigator.userAgent,
        isWalletBrowser: isWalletBrowser(),
        screen: {
          width: window.screen.width,
          height: window.screen.height
        }
      });
    }
  }, []);

  return (
    <>
      <GoogleTag measurementId="G-GZS7B7PFD0" />
      <OrganizationSchema />
      <ErrorBoundary>
        <div className="overflow-x-hidden">
          <BrowserRouter>
            <PageViewTracker />
            <TooltipProvider>
              <UserAuthProvider>
              <BurnSettingsProvider>
                <ToolsSettingsProvider>
                  <LockerSettingsProvider>
                    <LiquiditySettingsProvider>
                      <ContractScannerSettingsProvider>
                        <LiquidityProvider>
                          <FirebaseFeeProvider>
                            <GoogleAnalytics measurementId={import.meta.env.VITE_GA_MEASUREMENT_ID} />
                            <Routes>
                              {/* Legacy route redirects */}
                              <Route path="/bscchain/create" element={<Navigate to="/create" replace />} />
                              <Route path="/bscchain/tokentools" element={<Navigate to="/tokentools" replace />} />
                              <Route path="/bscchain/smartcontractlocker" element={<Navigate to="/smartcontractlocker" replace />} />
                              <Route path="/bscchain/mdbmatrixcontractscan" element={<Navigate to="/mdbmatrixcontractscan" replace />} />
                              <Route path="/bscchain/blog/*" element={<Navigate to="/blog/*" replace />} />
                              <Route path="/bscchain/admin/*" element={<Navigate to="/admin/*" replace />} />
                              <Route path="/bsc/*" element={<Navigate to="/" replace />} />
                              
                              {/* External App Routes (No Layout) */}
                              <Route path="/solanatokenlauncherapp" element={<SolanaTokenLauncherApp />} />
                              <Route path="/suitokenlauncher" element={<SuiTokenLauncher />} />
                              
                              {/* Main routes */}
                              <Route element={<BaseLayout />}>
                                <Route path="/" element={<HomePage />} />
                                <Route path="/create" element={<CreateTokenPage />} />
                                <Route path="/smartcontractlocker" element={<SmartContractLocker />} />
                                <Route path="/tokentools" element={<TokenTools />} />
                                <Route path="/tokenlogo" element={<TokenLogoPage />} />
                                <Route path="/mdbmatrixcontractscan" element={<MdbMatrixContractScan />} />
                                <Route path="/nft" element={<NFTPage />} />
                                <Route path="/multisender" element={<MultisenderPage />} />
                                <Route path="/liquidity" element={<LiquidityPage />} />
                                <Route path="/account" element={<AccountPage />} />
                                <Route path="/account/settings" element={<AccountPage />} />
                                <Route path="/account/referrals" element={<AccountPage />} />
                                <Route path="/account/app-settings" element={<AccountPage />} />
                                
                                {/* Legal Pages */}
                                <Route path="/about" element={<About />} />
                                <Route path="/contact" element={<Contact />} />
                                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                <Route path="/terms-of-service" element={<TermsOfService />} />
                                <Route path="/refund-policy" element={<RefundPolicy />} />
                                
                                {/* Admin Routes */}
                                <Route path="/admin" element={
                                  <ProtectedRoute>
                                    <AdminLayout />
                                  </ProtectedRoute>
                                }>
                                  <Route index element={<AdminPage />} />
                                  <Route path="dashboard" element={<AdminPage />} />
                                </Route>
                                
                                {/* Blog Routes */}
                                <Route path="/blog" element={<BlogLayout />}>
                                  <Route index element={<CreateTokenGuide />} />
                                  <Route path="create-token-guide" element={<CreateTokenGuide />} />
                                  <Route path="token-economics" element={<TokenEconomics />} />
                                  <Route path="token-security" element={<TokenSecurity />} />
                                  <Route path="automatic-contract-verification" element={<AutomaticContractVerification />} />
                                  <Route path="matrix-contract-scanner" element={<MatrixContractScanner />} />
                                  <Route path="token-tools" element={<TokenToolsBlog />} />
                                  <Route path="contract-locker" element={<ContractLockerBlog />} />
                                  <Route path="smart-contract-locking" element={<SmartContractLocking />} />
                                  <Route path="add-token-logo" element={<AddTokenLogo />} />
                                  <Route path="launch-on-pancakeswap" element={<LaunchOnPancakeSwap />} />
                                  <Route path="best-cold-storage-wallets" element={<BestColdStorageWallets />} />
                                  <Route path="create-meme-coin" element={<CreateMemeCoin />} />
                                  <Route path="best-crypto-project-app-2025" element={<BestCryptoProjectApp2025 />} />
                                  <Route path="create-reflection-token" element={<CreateReflectionToken />} />
                                  <Route path="create-auto-liquidity-token" element={<CreateAutoLiquidityToken />} />
                                  <Route path="create-auto-burn-token" element={<CreateAutoBurnToken />} />
                                  <Route path="solana-token-launcher" element={<SolanaTokenLauncher />} />
                                  <Route path="free-solana-tokens" element={<FreeSolanaTokens />} />
                                </Route>
                              </Route>
                            </Routes>
                            <Toaster />
                          </FirebaseFeeProvider>
                        </LiquidityProvider>
                      </ContractScannerSettingsProvider>
                    </LiquiditySettingsProvider>
                  </LockerSettingsProvider>
                </ToolsSettingsProvider>
              </BurnSettingsProvider>
              </UserAuthProvider>
            </TooltipProvider>
          </BrowserRouter>
        </div>
      </ErrorBoundary>
    </>
  );
}

export default App;